<template>
  <div ref="layerView" class="layer-view" v-show="mapLayers.length > 0">
    <div class="button-container">
      <md-button
        @click="toggleLayers"
        v-show="map && mapLayers.length > 0"
        ref="layersButton"
        class="md-icon-button md-raised layers-button"
      >
        <md-icon>{{ showLayerView ? "layers_clear" : "layers" }}</md-icon>
        <md-tooltip md-direction="left">
          Layers
        </md-tooltip>
      </md-button>
    </div>
    <md-list class="md-dense layer-control" v-show="showLayerView">
      <draggable v-model="mapLayersArray" handle=".handle" @change="updateMap">
        <template v-for="mapLayer in mapLayersArray">
          <md-list-item :key="mapLayer.mapLayerID" handle=".handle">
            <span class="handle">
              <md-icon>
                drag_handle
              </md-icon>
              <md-tooltip>
                Rearrange layer order
              </md-tooltip>
            </span>
            <div class="list-item-text-container">
              <span class="md-list-item-text">
                {{ mapLayer.isGroup ? mapLayer.blockName : mapLayer.layers[0].label }}
                <md-tooltip md-direction="bottom">
                  {{ mapLayer.isGroup ? mapLayer.blockName : mapLayer.layers[0].label }}
                </md-tooltip>
              </span>
            </div>
            <div v-if="mapLayer.isGroup">
              <input
                type="range"
                v-model.number="mapLayer.maxOpacity"
                min="0"
                max="1"
                step="0.01"
              />
              <md-tooltip>
                Change opacity of timeseries
              </md-tooltip>
            </div>
            <div v-else>
              <input
                type="range"
                v-model.number="mapLayer.layers[0].opacity"
                min="0"
                max="1"
                step="0.01"
              />
              <md-tooltip>
                Change opacity of layer
              </md-tooltip>
            </div>
            <md-button
              class="md-icon-button"
              @click="toggleLayerVisibility(mapLayer)"
            >
              <md-icon
                v-if="
                  mapLayer.isGroup
                    ? mapLayer.maxOpacity > 0
                    : mapLayer.layers[0].opacity > 0
                "
              >
                visibility
              </md-icon>
              <md-icon v-else> visibility_off </md-icon>
              <md-tooltip md-direction="bottom">
                Toggle visibility of layer
              </md-tooltip>
            </md-button>
            <md-button
              class="md-icon-button"
              @click="
                !mapLayer.isGroup
                  ? removeLayer(mapLayer, mapLayer.layers[0])
                  : removeGroup(mapLayer)
              "
            >
              <md-icon> clear </md-icon>
              <md-tooltip md-direction="bottom">
                Remove layer from image
              </md-tooltip>
            </md-button>
          </md-list-item>
        </template>
      </draggable>
    </md-list>
  </div>
</template>

<script>
/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */
import draggable from "vuedraggable";
import { AreaService }  from '@/services/area.service';
import bboxPolygon from "@turf/bbox-polygon";
import intersect from "@turf/intersect";

export default {
  components: {
    draggable,
  },
  props: {
    google: {
      type: Object,
      required: true,
    },
    map: {
      type: Object,
      required: true,
    },
    mapLayers: {
      type: Array,
      required: true,
    },
    mapBBox: {
      required: true,
    },
  },
  data() {
    return {
      showLayerView: true,
      mapLayersArray: []
    };
  },
  methods: {
    toggleLayers() {
      this.showLayerView = !this.showLayerView;
    },
    removeLayer(mapLayer, layer) {
      if (mapLayer.isGroup) {
        let index = mapLayer.layers.indexOf(layer);
        if (index > -1) {
          mapLayer.layers.splice(index, 1);
        }
      } else {
        let index = this.mapLayers.findIndex(
          (layer) => layer.mapLayerID === mapLayer.mapLayerID
        );
        if (index !== -1) {
          this.mapLayers.splice(index, 1);
        }
      }
      if (!this.mapLayers.length) {
        this.showLayerView = false;
      }
      this.$emit('layer-removed', mapLayer.blockID)
    },
    removeGroup(mapLayer) {
      let index = this.mapLayers.findIndex(
        (layer) => layer.mapLayerID === mapLayer.mapLayerID
      );
      if (index !== -1) {
        this.mapLayers.splice(index, 1);
      }
      this.$emit('layer-removed', mapLayer.blockID)
    },
    updateMap() {
      this.$emit(
        "update-map-layers",
        this.mapLayersArray,
        this.map.overlayMapTypes
      );
    },
    toggleLayerVisibility(mapLayer) {
      if (mapLayer.isGroup) {
        if (mapLayer.maxOpacity > 0) {
          mapLayer.maxOpacity = 0;
        } else {
          mapLayer.maxOpacity = 1;
        }
      } else {
        mapLayer.layers.forEach((layer) => {
          if (layer.opacity > 0) {
            layer.opacity = 0;
          } else {
            layer.opacity = 1;
          }
        });
      }
    }
  },
  mounted() {
    const layerView = this.$refs.layerView;
    this.map.controls[this.google.maps.ControlPosition.RIGHT_TOP].push(
      layerView
    );

    // subscribes to dataset bbox updates from output_data_selector block to create/remove bboxes from map
    this.updatedDatasetBbox = AreaService.updatedDatasetBbox$.subscribe((params) => {
      let bbox = params.bbox;

      // checks that bbox exists
      if(bbox !== null) {
        // gets bbox of map and of dataset and converts to geojson polygon
        let polygonBbox = bboxPolygon(bbox)
        let mapBounds = this.map.getBounds();
        let NE = mapBounds.getNorthEast();
        let SW = mapBounds.getSouthWest();

        let mapBbox = [
          NE.lng(),
          NE.lat(),
          SW.lng(),
          SW.lat()
        ]
        mapBbox = bboxPolygon(mapBbox);

        // checks if user is already within bbox, if not pan to the bbox
        if(intersect(mapBbox, polygonBbox) === null){
          var bounds = new this.google.maps.LatLngBounds(
            new this.google.maps.LatLng({lng: bbox[0], lat: bbox[1]}),
            new this.google.maps.LatLng({lng: bbox[2], lat: bbox[3]})
          )

          this.map.fitBounds(bounds);
        }
      }
    })
  },
  watch: {
    mapBBox: {
      immediate: true,
      handler(mapBBox) {
        const isBboxValid = (mapBBox) => {
          const validatorFunc = (pair) => {
            return pair.every((value) => isFinite(value));
          };

          if (mapBBox !== null) {
            return mapBBox.every(validatorFunc);
          }

          return false;
        };

        if (mapBBox !== null && isBboxValid(mapBBox)) {
          var bounds = new this.google.maps.LatLngBounds(
            new this.google.maps.LatLng(mapBBox[0][1], mapBBox[0][0]),
            new this.google.maps.LatLng(mapBBox[1][1], mapBBox[1][0])
          );

          this.map.fitBounds(bounds, 0);

        }
      },
    },
    mapLayers: {
      immediate: true,
      handler(mapLayers){
        // reversed so in menu of layers top layer is top in menu
          let reversedMapLayers = mapLayers.slice().reverse();
          let isNewLayers = () => {
            let reset = false;
            console.log("maplayers array length", this.mapLayersArray.length)
            console.log("reversed map layers length", reversedMapLayers.length)

            if (this.mapLayersArray.length !== reversedMapLayers.length){
              return true;
            }

            reversedMapLayers.forEach((mapLayer, index) => {
              // checks if new map layers have been added
              // loop through each layer map id
              // check against map id
              mapLayer.layers.forEach((layer, kIndex) => {
                if(layer.mapid !== this.mapLayersArray[index].layers[kIndex].mapid){
                  reset = true;
                }
              })
            });
            return reset;
          };

          if (isNewLayers()) {
            this.mapLayersArray = reversedMapLayers;
          }
      },
    },
  },
};
</script>